import MainHeading from "@/components/common/MainHeading"
import Layout from "@/components/layout/Layout"
import { LeaveService } from "@/services/Leave/LeaveSetting"
import { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { ThrowError } from "@/api/callApi"
import type { TableColumn } from "@/components/common/CommonTable"
import { Button } from "@/components/ui/button"
import { RoutesLink } from "@/utility/RouteLinks"
import { Eye, Plus, Trash, TriangleAlert, MoreHorizontal } from "lucide-react"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import CommonTable from "@/components/common/CommonTable"
import CommonModal from "@/components/common/CommonModal"
import type { ILeave } from "@/interface/leave"
import moment from "moment"
import { useAppSelector } from "@/store/store"
import CommonFilter from "@/components/common/CommonFilter"
import type { IFilter, IPageInfo } from "@/interface/common.interface"
import { GetFilterUrl, showToastNotification } from "@/utility/helper"
import TablePagination from "@/components/common/TablePagination"

const DURATION_OPTIONS = [
    { label: "Full Day", value: "FULL_DAY" },
    { label: "Half Day", value: "HALF_DAY" },
    { label: "First Half", value: "FIRST_HALF" },
    { label: "Second Half", value: "SECOND_HALF" },
];

const MyLeave: React.FC = () => {
    const navigate = useNavigate()
    const loggedInUser = useAppSelector((state) => state.user.user)
    const [leaves, setLeaves] = useState<ILeave[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<string>("");

    // Unified filter state
    const [filter, setFilter] = useState<IFilter>({
        search: "",
        startDate: "",
        endDate: "",
        duration: "",
    });

    const [paginationData, setPaginationData] = useState<IPageInfo>({
        currentPage: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
    });

    const handleChangePageSize = (value: number) => {
        setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
    };

    const toggleDeleteDialog = useCallback((id: string = "") => {
        setDeleteId(id ?? "");
        setDeleteDialogOpen((prev) => !prev);
    }, []);

    const handleSearchChange = useCallback((value: string) => {
        setFilter((prev) => {
            if (prev.search === value) return prev;
            return { ...prev, search: value };
        });
    }, []);

    const handleFilterChange = useCallback((value: string, name: string) => {
        setFilter((prev) => ({ ...prev, [name]: value }));
    }, []);

    const handleDateRangeChange = useCallback((start: string, end: string) => {
        setFilter((prev) => ({ ...prev, startDate: start, endDate: end }));
    }, []);

    const handleGetLeaves = async (currentPage = paginationData.currentPage, currentLimit = paginationData.pageSize) => {
        setIsLoading(true);
        try {
            const params = GetFilterUrl({ ...filter });
            const userIdParam = loggedInUser?.id ? `&userId=${loggedInUser.id}` : "";
            const PATH = `pageNumber=${currentPage}&pageSize=${currentLimit}${params ? `&${params}` : ""}${userIdParam}`;
            console.log("filterQuery", PATH);

            const response = await LeaveService.getLeaves(PATH);
            if (response?.status) {
                const allLeaves = response?.data?.leaves || response?.data || [];
                const userLeaves = Array.isArray(allLeaves)
                    ? allLeaves.filter((l: ILeave) => !loggedInUser?.id || l.userId === loggedInUser.id)
                    : [];
                setLeaves(userLeaves);
                setPaginationData((prev) => ({
                    ...prev,
                    total: response?.data?.pageInfo?.total || 0,
                    totalPages: response?.data?.pageInfo?.totalPages || 0,
                }));
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        handleGetLeaves(paginationData.currentPage, paginationData.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationData.currentPage, paginationData.pageSize, filter]);

    const handleDeleteLeave = useCallback(async () => {
        try {
            const response = await LeaveService.deleteLeave(deleteId);
            if (response?.status) {
                showToastNotification("success", response?.message || "Leave deleted successfully");
                toggleDeleteDialog("");
                handleGetLeaves();
            } else {
                showToastNotification("danger", response?.message || "Failed to delete leave");
            }
        } catch (error) {
            ThrowError(error);
        }
    }, [deleteId, toggleDeleteDialog]);
    const columns: TableColumn<ILeave>[] = [
        {
            key: 'srNo',
            header: 'Sr No',
            render: (_item: ILeave, index: number) => (
                <span className="text-muted-foreground font-medium"># {index + 1}</span>
            )
        },
        {
            key: 'Name',
            header: 'Name',
            render: (item: ILeave) => (
                <span className="font-semibold capitalize">{item?.user?.name || "—"}</span>
            )
        },
        {
            key: 'site',
            header: 'Site',
            render: (item: ILeave) => (
                <span className="font-semibold capitalize">{item?.site?.name || "—"}</span>
            )
        },
        {
            key: 'branch',
            header: 'Branch',
            render: (item: ILeave) => (
                <span className="font-semibold capitalize">{item?.branch?.name || "—"}</span>
            )
        },
        {
            key: 'leaveType',
            header: 'Leave Type',
            render: (item: ILeave) => (
                <span className="font-medium">{item?.leaveType || "—"}</span>
            )
        },
        {
            key: 'startDate',
            header: 'Start Date',
            render: (item: ILeave) => (
                <span className="font-medium">
                    {item?.startDate ? moment(item.startDate).format("DD-MM-YYYY") : "—"}
                </span>
            )
        },
        {
            key: 'endDate',
            header: 'End Date',
            render: (item: ILeave) => (
                <span className="font-medium">
                    {item?.endDate ? moment(item.endDate).format("DD-MM-YYYY") : "—"}
                </span>
            )
        },
        {
            key: 'duration',
            header: 'Duration',
            render: (item: ILeave) => (
                <span className="font-medium capitalize text-slate-600">
                    {item?.duration ? item.duration.replace(/_/g, " ") : "—"}
                </span>
            )
        },
        {
            key: 'numberOfDays',
            header: 'Days',
            render: (item: ILeave) => (
                <span className="font-medium">{item?.numberOfDays || 0}</span>
            )
        },
        {
            key: 'status',
            header: 'Status',
            render: (item: ILeave) => {
                const status = (item?.status || "pending").toLowerCase();
                let statusClasses = "text-amber-600 border-amber-600 bg-amber-50";
                if (status === "approved" || status === "active") {
                    statusClasses = "text-green-600 border-green-600 bg-green-50";
                } else if (status === "rejected" || status === "cancelled") {
                    statusClasses = "text-destructive border-destructive bg-destructive/10";
                }
                return (
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full capitalize ${statusClasses}`}>
                        {status}
                    </span>
                )
            }
        },
        {
            key: 'actions',
            header: 'Actions',
            render: (item: ILeave) => {
                const isPending = (item?.status || "pending").toLowerCase() === "pending";
                return (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-muted">
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal className="h-4 w-4 text-slate-500" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-[170px] rounded-xl shadow-lg border-slate-100">
                            <DropdownMenuItem
                                onClick={() => { if (item?.id) navigate(`${RoutesLink.leaveDetail}/${item.id}`); }}
                                className="cursor-pointer py-2 px-3 hover:bg-slate-50 focus:bg-slate-50"
                            >
                                <Eye className="h-4 w-4 mr-2" />View Details
                            </DropdownMenuItem>
                            {isPending && (
                                <>

                                    <DropdownMenuItem
                                        onClick={() => { if (item?.id) toggleDeleteDialog(item.id); }}
                                        className="cursor-pointer py-2 px-3 hover:bg-slate-50 focus:bg-slate-50 text-red-600 focus:text-red-600"
                                    >
                                        <Trash className="h-4 w-4" />Delete Leave
                                    </DropdownMenuItem>
                                </>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            }
        }
    ]

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title="My Leaves"
                    buttonText="Apply Leave"
                    buttonIcon={<Plus size={18} />}
                    isBackButtonVisible={true}
                    onClick={() => navigate(RoutesLink.addLeave)}
                />

                <CommonFilter
                    searchValue={filter.search || ""}
                    onSearchChange={handleSearchChange}
                    startDate={filter.startDate || ""}
                    endDate={filter.endDate || ""}
                    onDateRangeChange={handleDateRangeChange}
                    value={filter}
                    filterOptions={DURATION_OPTIONS}
                    filterName="duration"
                    filterPlaceholder="Select Duration"
                    onChange={handleFilterChange}
                />
                <CommonTable
                    columns={columns}
                    data={leaves}
                    loading={isLoading}
                />
                <TablePagination
                    paginationData={paginationData}
                    setPaginationData={setPaginationData}
                    handleChangePageSize={handleChangePageSize}
                    isLoading={isLoading}
                />

                <CommonModal
                    show={deleteDialogOpen}
                    onClose={() => toggleDeleteDialog("")}
                    title=""
                    className="max-w-md rounded-2xl p-0 overflow-hidden"
                >
                    <div className="p-6">
                        <div className="flex flex-col items-center text-center">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                                <TriangleAlert className="text-red-600 w-8 h-8" />
                            </div>
                            <h2 className="mt-5 text-2xl font-bold text-gray-900">Delete Applied Leave?</h2>
                            <p className="mt-3 text-sm leading-6 text-gray-500">
                                This action cannot be undone. Once deleted, the leave application will be permanently removed.
                            </p>
                        </div>
                        <div className="mt-8 flex gap-3">
                            <Button variant="outline" className="flex-1" onClick={() => toggleDeleteDialog("")}>
                                Cancel
                            </Button>
                            <Button variant="destructive" className="flex-1" onClick={handleDeleteLeave}>
                                Delete
                            </Button>
                        </div>
                    </div>
                </CommonModal>
            </div>
        </Layout>
    )
}

export default MyLeave